import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProfileHeader from './components/ProfileHeader';
import ProfileTabs from './components/ProfileTabs';
import PostsTab from './components/PostsTab';
import PhotosTab from './components/PhotosTab';
import FriendsTab from './components/FriendsTab';
import EditProfileModal from './components/EditProfileModal';
import PostCreator from '../news-feed/components/PostCreator';
import ThreeBackground from '../aether/components/ThreeBackground';

import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../lib/profileService';
import { postService } from '../../lib/postService';
import { friendService } from '../../lib/friendService';

const UserProfile = () => {
  const { user: authUser, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('posts');
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [userPosts, setUserPosts] = useState([]);
  const [userPhotos, setUserPhotos] = useState([]);
  const [userFriends, setUserFriends] = useState([]);

  // Use auth user data
  const currentUser = authUser ? {
    id: authUser.id,
    name: authUser.user_metadata?.full_name || authUser.email,
    username: authUser.user_metadata?.username || authUser.email.split('@')[0],
    avatar: authUser.user_metadata?.avatar_url || "https://images.unsplash.com/photo-1724128195747-dd25cba7860f",
    avatarAlt: 'User avatar'
  } : null;

  // Get profile ID from URL params, default to current user
  const profileId = searchParams?.get('id') || currentUser?.id;
  const isOwnProfile = profileId === currentUser?.id;

  // Map profile data
  const userData = profileData ? {
    id: profileData.id,
    name: profileData.full_name,
    username: profileData.username,
    profilePhoto: profileData.avatar_url || (isOwnProfile ?
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face' :
    'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200&h=200&fit=crop&crop=face'),
    profilePhotoAlt: 'Profile photo',
    bio: profileData.bio || 'No bio yet.',
    friendsCount: profileData.friends_count || 0,
    location: profileData.location || 'Unknown',
    joinedDate: new Date(profileData.created_at).toLocaleDateString()
  } : {
    id: profileId,
    name: isOwnProfile ? 'John Doe' : 'Sarah Wilson',
    username: isOwnProfile ? 'johndoe' : 'sarahwilson',
    profilePhoto: isOwnProfile ?
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face' :
    'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200&h=200&fit=crop&crop=face',
    profilePhotoAlt: isOwnProfile ?
    'Professional headshot of a man with short brown hair wearing a navy blue suit' : 'Professional headshot of a woman with long blonde hair wearing a white blouse',
    bio: isOwnProfile ?
    'Software developer passionate about creating amazing user experiences. Love hiking, photography, and good coffee ☕' : 'Digital marketing specialist | Travel enthusiast | Dog lover 🐕 | Always exploring new places and cultures',
    friendsCount: isOwnProfile ? 342 : 156,
    location: isOwnProfile ? 'San Francisco, CA' : 'New York, NY',
    joinedDate: isOwnProfile ? 'March 2020' : 'July 2021'
  };

  // Mock friendship status for non-own profiles
  const [friendshipStatus, setFriendshipStatus] = useState(
    isOwnProfile ? null : 'none' // none, pending, received, friends
  );

  // Mock posts data
  const postsData = [
  {
    id: 'post-1',
    author: {
      name: userData?.name,
      avatar: userData?.profilePhoto,
      avatarAlt: userData?.profilePhotoAlt
    },
    content: `Just finished an amazing hike at Golden Gate Park! The weather was perfect and the views were incredible. There's nothing like spending time in nature to recharge and get inspired. 🌲✨\n\nAlready planning my next adventure - thinking about exploring some trails in Marin County next weekend. Any recommendations?`, image: "https://images.unsplash.com/photo-1698253968361-fe8b24636d25", imageAlt: 'Scenic mountain trail with lush green trees and a winding path leading to distant hills',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    likes: 24,
    comments: [
    {
      id: 'comment-1',
      author: {
        name: 'Mike Johnson', avatar: "https://images.unsplash.com/photo-1735081204311-eaaecd5b1782", avatarAlt: 'Headshot of a young man with dark hair and a friendly smile'
      },
      content: 'Looks amazing! I love that trail too.',
      timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000)
    },
    {
      id: 'comment-2',
      author: {
        name: 'Emma Davis', avatar: "https://images.unsplash.com/photo-1705841853374-75eacb92e2bd", avatarAlt: 'Professional headshot of a woman with curly brown hair wearing glasses'
      },
      content: 'Try the Muir Woods trail - it\'s breathtaking!',
      timestamp: new Date(Date.now() - 30 * 60 * 1000)
    }],

    shares: 3,
    isLiked: false
  },
  {
    id: 'post-2',
    author: {
      name: userData?.name,
      avatar: userData?.profilePhoto,
      avatarAlt: userData?.profilePhotoAlt
    },
    content: `Excited to share that I just launched my new portfolio website! 🚀 It's been months of work, but I'm finally happy with the design and functionality.\n\nBuilt with React, TypeScript, and a lot of coffee. Check it out and let me know what you think!`,
    timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
    likes: 47,
    comments: [
    {
      id: 'comment-3',
      author: {
        name: 'Alex Chen',
        avatar: "https://images.unsplash.com/photo-1687256457585-3608dfa736c5",
        avatarAlt: 'Professional headshot of an Asian man with short black hair in business attire'
      },
      content: 'Congratulations! The design looks fantastic.',
      timestamp: new Date(Date.now() - 20 * 60 * 60 * 1000)
    }],

    shares: 8,
    isLiked: true
  },
  {
    id: 'post-3',
    author: {
      name: userData?.name,
      avatar: userData?.profilePhoto,
      avatarAlt: userData?.profilePhotoAlt
    },
    content: `Weekend coffee brewing session ☕ Trying out a new Ethiopian single origin from my local roaster. The floral notes are incredible!\n\nThere's something therapeutic about the whole process - from grinding the beans to that first perfect sip. What's your favorite brewing method?`,
    image: "https://images.unsplash.com/photo-1723051373606-603cdec36495",
    imageAlt: 'Overhead view of coffee brewing setup with pour-over dripper, coffee beans, and steaming cup',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
    likes: 18,
    comments: [],
    shares: 2,
    isLiked: false
  }];


  // Mock photos data
  const photosData = [
  {
    id: 'photo-1',
    url: "https://images.unsplash.com/photo-1558211245-300c69d87b45",
    alt: 'Stunning mountain landscape with snow-capped peaks reflected in a crystal clear alpine lake',
    caption: 'Amazing sunset at the mountains',
    uploadDate: 'October 15, 2024'
  },
  {
    id: 'photo-2',
    url: "https://images.unsplash.com/photo-1696708422900-41ef68f6b125",
    alt: 'Scenic hiking trail winding through dense forest with dappled sunlight filtering through trees',
    caption: 'Perfect hiking weather',
    uploadDate: 'October 12, 2024'
  },
  {
    id: 'photo-3',
    url: "https://images.unsplash.com/photo-1566314067000-c4f342de9b80",
    alt: 'Artisanal coffee setup with pour-over dripper, freshly ground beans, and elegant ceramic cup',
    caption: 'Morning coffee ritual',
    uploadDate: 'October 10, 2024'
  },
  {
    id: 'photo-4',
    url: "https://images.unsplash.com/photo-1493408425253-c9c30e1d0f7d",
    alt: 'Modern workspace with laptop, notebook, and coffee cup arranged on a clean wooden desk',
    caption: 'New workspace setup',
    uploadDate: 'October 8, 2024'
  },
  {
    id: 'photo-5',
    url: "https://images.unsplash.com/photo-1599404127923-b50bb4e97c0c",
    alt: 'Golden hour cityscape with tall buildings silhouetted against a vibrant orange and pink sky',
    caption: 'City lights at golden hour',
    uploadDate: 'October 5, 2024'
  },
  {
    id: 'photo-6',
    url: "https://images.unsplash.com/photo-1564954061407-9c8a06424655",
    alt: 'Peaceful forest path with tall trees creating a natural canopy overhead',
    caption: 'Forest adventures',
    uploadDate: 'October 2, 2024'
  }];


  // Mock friends data
  const friendsData = [
  {
    id: 'friend-1',
    name: 'Emily Rodriguez',
    avatar: "https://images.unsplash.com/photo-1734521992144-5a4d0ea55952",
    avatarAlt: 'Professional headshot of a Hispanic woman with long dark hair wearing a blue blazer',
    mutualFriends: 12
  },
  {
    id: 'friend-2',
    name: 'Michael Chen',
    avatar: "https://images.unsplash.com/photo-1687256457585-3608dfa736c5",
    avatarAlt: 'Professional headshot of an Asian man with short black hair wearing a gray suit',
    mutualFriends: 8
  },
  {
    id: 'friend-3',
    name: 'Jessica Thompson',
    avatar: "https://images.unsplash.com/photo-1671477827852-fde9a7b874bd",
    avatarAlt: 'Professional headshot of a woman with curly blonde hair wearing glasses and a white shirt',
    mutualFriends: 15
  },
  {
    id: 'friend-4',
    name: 'David Kim',
    avatar: "https://images.unsplash.com/photo-1734456611474-13245d164868",
    avatarAlt: 'Professional headshot of a Korean man with short dark hair wearing a navy blue suit',
    mutualFriends: 6
  },
  {
    id: 'friend-5',
    name: 'Amanda Foster',
    avatar: "https://images.unsplash.com/photo-1702089050621-62646a2b748f",
    avatarAlt: 'Professional headshot of a woman with straight brown hair wearing a black blazer',
    mutualFriends: 9
  },
  {
    id: 'friend-6',
    name: 'Ryan Martinez',
    avatar: "https://images.unsplash.com/photo-1704731297889-6d54dc06da74",
    avatarAlt: 'Professional headshot of a Latino man with short brown hair wearing a white shirt',
    mutualFriends: 11
  }];


  const fetchProfile = useCallback(async () => {
    if (!profileId) return;
    
    try {
      const [data, allPosts, friendsList] = await Promise.all([
        profileService.getProfile(profileId).catch(err => {
          console.warn("Failed to fetch profile from DB, using fallback metadata:", err);
          return {
            id: profileId,
            full_name: currentUser?.name || 'User',
            username: currentUser?.username || 'user',
            avatar_url: currentUser?.avatar || "https://images.unsplash.com/photo-1724128195747-dd25cba7860f",
            bio: 'Hello! I am new here.',
            friends_count: 0,
            location: 'Earth',
            created_at: new Date().toISOString()
          };
        }),
        postService.getPosts(),
        friendService.getFriends(profileId).catch(err => {
          console.warn("Failed to fetch friends from DB, using empty fallback:", err);
          return [];
        })
      ]);
      
      setProfileData(data);
      setUserFriends(friendsList || []);
      
      // Filter posts for this user
      const thisUserPosts = (allPosts || []).filter(post => post.user_id === profileId || post.author?.id === profileId);
      setUserPosts(thisUserPosts);
      
      // Extract photos and videos from posts
      const photos = thisUserPosts
        .filter(post => post.image_url || post.video_url)
        .map(post => ({
          id: post.id,
          url: post.image_url || post.video_url,
          type: post.media_type || (post.video_url ? 'video' : 'image'),
          alt: 'User uploaded media',
          caption: post.content,
          uploadDate: new Date(post.created_at).toLocaleDateString()
        }));
      setUserPhotos(photos);
      
      // Fetch friendship status if not own profile
      if (!isOwnProfile && currentUser) {
        const status = await friendService.getFriendshipStatus(currentUser.id, profileId);
        setFriendshipStatus(status || 'none');
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  }, [profileId, currentUser, isOwnProfile]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Listen for local post creation events to refresh the feed instantly
  useEffect(() => {
    const handleLocalNewPost = () => {
      fetchProfile();
    };
    window.addEventListener('new-post-created', handleLocalNewPost);
    return () => window.removeEventListener('new-post-created', handleLocalNewPost);
  }, [fetchProfile]);

  // Real-time updates for posts
  useEffect(() => {
    if (!profileId) return;
    
    const subscription = postService.subscribeToPosts(async (payload) => {
      // Re-fetch all posts to keep data consistent (including author data)
      const allPosts = await postService.getPosts();
      const thisUserPosts = (allPosts || []).filter(post => post.user_id === profileId || post.author?.id === profileId);
      setUserPosts(thisUserPosts);
      
      const photos = thisUserPosts
        .filter(post => post.image_url || post.video_url)
        .map(post => ({
          id: post.id,
          url: post.image_url || post.video_url,
          type: post.media_type || (post.video_url ? 'video' : 'image'),
          alt: 'User uploaded media',
          caption: post.content,
          uploadDate: new Date(post.created_at).toLocaleDateString()
        }));
      setUserPhotos(photos);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [profileId]);

  const handleEditProfile = () => {
    setIsEditModalOpen(true);
  };

  const handleProfileUpdate = (updatedUser) => {
    // Update local profile data state
    setProfileData(prev => ({
      ...prev,
      full_name: updatedUser.name,
      username: updatedUser.username,
      bio: updatedUser.bio,
      location: updatedUser.location
    }));
  };

  const handleAvatarUpload = async (file) => {
    try {
      const publicUrl = await profileService.uploadAvatar(currentUser.id, file);
      await profileService.updateProfile(currentUser.id, { avatar_url: publicUrl });
      setProfileData(prev => ({ ...prev, avatar_url: publicUrl }));
    } catch (error) {
      console.error("Failed to upload avatar", error);
    }
  };

  const handleBioUpdate = async (bioText) => {
    try {
      await profileService.updateProfile(currentUser.id, { bio: bioText });
      setProfileData(prev => ({ ...prev, bio: bioText }));
    } catch (error) {
      console.error("Failed to update bio", error);
    }
  };

  const handleFriendAction = async (status) => {
    if (!currentUser || !profileId) return;

    try {
      if (status === 'none') {
        await friendService.sendFriendRequest(currentUser.id, profileId);
        setFriendshipStatus('pending');
      } else if (status === 'received') {
        await friendService.acceptRequestBetween(profileId, currentUser.id);
        setFriendshipStatus('friends');
        // Refresh friends list to show the new friend
        const updatedFriends = await friendService.getFriends(profileId);
        setUserFriends(updatedFriends || []);
        // Increment friends count locally
        setProfileData(prev => ({
          ...prev,
          friends_count: (prev?.friends_count || 0) + 1
        }));
      } else if (status === 'friends') {
        await friendService.unfriend(currentUser.id, profileId);
        setFriendshipStatus('none');
        // Refresh friends list
        const updatedFriends = await friendService.getFriends(profileId);
        setUserFriends(updatedFriends || []);
        // Decrement friends count locally
        setProfileData(prev => ({
          ...prev,
          friends_count: Math.max(0, (prev?.friends_count || 0) - 1)
        }));
      }
    } catch (err) {
      console.error("Failed to perform friend action:", err);
    }
  };

  const handleLike = (postId) => {
    console.log('Like post:', postId);
    // In a real app, this would update the post's like status
  };

  const handleComment = (postId, comment) => {
    console.log('Comment on post:', postId, comment);
    // In a real app, this would add the comment to the post
  };

  const handleShare = (postId) => {
    console.log('Share post:', postId);
    // In a real app, this would open share options
  };

  const handleRemoveFriend = async (friendId) => {
    if (!currentUser) return;
    try {
      await friendService.unfriend(currentUser.id, friendId);
      const updatedFriends = await friendService.getFriends(currentUser.id);
      setUserFriends(updatedFriends || []);
      setProfileData(prev => ({
        ...prev,
        friends_count: Math.max(0, (prev?.friends_count || 0) - 1)
      }));
    } catch (err) {
      console.error("Failed to remove friend:", err);
    }
  };

  const handleMessage = (userId) => {
    console.log('Message user:', userId);
    // In a real app, this would open messaging interface
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-on-background">
        <ThreeBackground />
        <div className="relative z-10">
          <Header user={currentUser} onLogout={handleLogout} />
          <div className="pt-24 pb-8">
            <div className="max-w-4xl mx-auto px-4">
              <div className="animate-pulse space-y-6">
                <div className="glass-panel rounded-xl p-6">
                  <div className="flex items-end space-x-6">
                    <div className="w-40 h-40 bg-white/10 rounded-full rim-light"></div>
                    <div className="flex-1 space-y-3">
                      <div className="h-8 bg-white/10 rounded w-1/3"></div>
                      <div className="h-4 bg-white/10 rounded w-2/3"></div>
                      <div className="h-4 bg-white/10 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
                <div className="glass-panel rounded-xl p-4">
                  <div className="flex space-x-1">
                    {[1, 2, 3]?.map((i) =>
                    <div key={i} className="flex-1 h-12 bg-white/10 rounded"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>);
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'posts':
        return (
          <PostsTab
            posts={userPosts}
            onLike={handleLike}
            onComment={handleComment}
            onShare={handleShare} />);


      case 'photos':
        return (
          <PhotosTab
            photos={userPhotos}
            isOwnProfile={isOwnProfile} />);


      case 'friends':
        return (
          <FriendsTab
            friends={userFriends.length > 0 ? userFriends.map(f => ({
              id: f.id,
              name: f.full_name,
              avatar: f.avatar_url || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face",
              avatarAlt: f.full_name,
              mutualFriends: 0
            })) : friendsData}
            isOwnProfile={isOwnProfile}
            onRemoveFriend={handleRemoveFriend}
            onMessage={handleMessage} />);


      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background text-on-background">
      <ThreeBackground />
      <div className="relative z-10">
        <Header user={currentUser} onLogout={handleLogout} />
        <div className="pt-24 pb-8">
          <div className="max-w-4xl mx-auto px-4 space-y-6">
            {/* Profile Header */}
            <div className="glass-panel rounded-xl overflow-hidden">
              <ProfileHeader
            user={userData}
            isOwnProfile={isOwnProfile}
            onEditProfile={handleEditProfile}
            onAvatarUpload={handleAvatarUpload}
            onBioUpdate={handleBioUpdate}
            onFriendAction={handleFriendAction}
            friendshipStatus={friendshipStatus} />
            </div>


          {/* Profile Tabs */}
          <ProfileTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            postsCount={userPosts?.length || 0}
            photosCount={userPhotos?.length || 0}
            friendsCount={userFriends.length > 0 ? userFriends.length : friendsData?.length} />


          {/* Post Creator (only if viewing own profile and on posts tab) */}
          {isOwnProfile && activeTab === 'posts' && (
            <PostCreator user={currentUser} />
          )}

            {/* Tab Content */}
            <div className="glass-panel rounded-xl p-4">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={userData}
        onUpdate={handleProfileUpdate}
        onAvatarUpload={handleAvatarUpload}
      />
    </div>
  );
};

export default UserProfile;